import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'constants/routes';
import { learnerNav } from 'constants/copy';
import { useParashariLearnNavOptional } from 'schools/parashari/contexts/ParashariLearnNavContext';
import {
  clearJyotishaTopicFromUrl,
  readJyotishaTopicFromLocation,
  readNakProfileFromLocation,
  replaceJyotishaTopicInUrl,
} from 'schools/parashari/utils/jyotishaTopicUrl';
import {
  getJyotishaTopicGroupLabelEn,
  getJyotishaTopicLabel,
} from 'schools/parashari/data/jyotishaTopicLabels';
import { JYOTISHA_MARGA_TAB_GROUPS } from 'schools/parashari/data/jyotishaMargaTabGroups';
import { getJaiminiChapter, getJaiminiTopic } from 'schools/jaimini/data/jaiminiSyllabus';
import { getBnnChapter, getBnnTopic } from 'schools/bnn/data/bnnSyllabus';
import { getLalKitabChapter, getLalKitabTopic } from 'schools/lalKitab/data/lalKitabSyllabus';
import { getKpChapter, getKpTopic } from 'schools/kp/data/kpSyllabus';
import { MODULE_PARAM, TOPIC_PARAM } from 'shared/schoolLearn/useSchoolLearnNav';
import { cn } from 'utils/cn';

function groupIdForTopic(topicId: string): string | null {
  for (const group of JYOTISHA_MARGA_TAB_GROUPS) {
    if (group.tabs.some((tab: { id: string }) => tab.id === topicId)) {
      return group.id;
    }
  }
  return null;
}

type Crumb = {
  label: string;
  onClick?: () => void;
};

const URL_CHANGE_EVENT = 'takshify-parashari-url-change';

const PAGE_CRUMBS: { match: (pathname: string) => boolean; label: string }[] = [
  { match: (p) => p === ROUTES.DASHBOARD || p.startsWith(`${ROUTES.DASHBOARD}/`), label: learnerNav.dashboard },
  { match: (p) => p === ROUTES.SCHOOLS || p.startsWith('/schools/'), label: learnerNav.courses },
  { match: (p) => p === ROUTES.PROGRAMS || p.startsWith(`${ROUTES.PROGRAMS}/`), label: learnerNav.programs },
  { match: (p) => p === ROUTES.BATCHES || p.startsWith(`${ROUTES.BATCHES}/`), label: learnerNav.batches },
  { match: (p) => p === ROUTES.COMMUNITY || p.startsWith(`${ROUTES.COMMUNITY}/`), label: learnerNav.community },
  { match: (p) => p === ROUTES.ASSESSMENTS || p.startsWith(`${ROUTES.ASSESSMENTS}/`), label: learnerNav.assessments },
  { match: (p) => p === ROUTES.CALENDAR, label: learnerNav.calendar },
  { match: (p) => p === ROUTES.REMINDERS, label: learnerNav.reminders },
  { match: (p) => p === ROUTES.SADHANA || p.startsWith(`${ROUTES.SADHANA}/`), label: learnerNav.sadhana },
  { match: (p) => p === ROUTES.CERTIFICATES, label: learnerNav.certificates },
  { match: (p) => p === ROUTES.BOOKMARKS, label: learnerNav.bookmarks },
  { match: (p) => p === ROUTES.NOTES, label: learnerNav.notes },
  { match: (p) => p === ROUTES.PRIVATE_NOTES, label: learnerNav.privateNotes },
  { match: (p) => p.startsWith('/ai'), label: learnerNav.aiTutor },
  { match: (p) => p === ROUTES.PROFILE, label: learnerNav.progress },
  { match: (p) => p === ROUTES.SETTINGS, label: learnerNav.settings },
];

function useParashariUrlRevision() {
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const bump = () => setRevision((n) => n + 1);
    window.addEventListener(URL_CHANGE_EVENT, bump);
    window.addEventListener('popstate', bump);
    return () => {
      window.removeEventListener(URL_CHANGE_EVENT, bump);
      window.removeEventListener('popstate', bump);
    };
  }, []);
  return revision;
}

function resolveGenericPageLabel(pathname: string): string {
  const hit = PAGE_CRUMBS.find((row) => row.match(pathname));
  return hit?.label ?? learnerNav.sidebarTitle;
}

function LocationTrail({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs">
      {items.map((item, i) => (
        <span key={`${item.label}-${i}`} className="flex min-w-0 items-center gap-1.5">
          {i > 0 ? (
            <span className="shrink-0 text-content-tertiary" aria-hidden>
              ›
            </span>
          ) : null}
          {item.onClick ? (
            <button
              type="button"
              onClick={item.onClick}
              className="truncate text-content-secondary transition-colors hover:text-content"
            >
              {item.label}
            </button>
          ) : (
            <span className="truncate font-medium text-content">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

type SchoolLearnCrumbConfig = {
  learnPath: string;
  schoolLabel: string;
  resolveTopic: (topicId: string) => { topicTitle: string; chapterId: string; chapterTitle: string } | null;
  resolveChapter: (moduleId: string) => string | null;
};

function buildSchoolLearnCrumbs(
  search: string,
  navigate: ReturnType<typeof useNavigate>,
  config: SchoolLearnCrumbConfig,
): Crumb[] {
  const params = new URLSearchParams(search);
  const topicId = params.get(TOPIC_PARAM);
  const moduleId = params.get(MODULE_PARAM);

  const goHub = () => {
    navigate(config.learnPath);
  };

  const goModule = (id: string) => {
    navigate(`${config.learnPath}?${MODULE_PARAM}=${encodeURIComponent(id)}`);
  };

  if (topicId) {
    const meta = config.resolveTopic(topicId);
    if (!meta) {
      return [{ label: config.schoolLabel }];
    }
    return [
      { label: config.schoolLabel, onClick: goHub },
      {
        label: meta.chapterTitle,
        onClick: () => goModule(meta.chapterId),
      },
      { label: meta.topicTitle },
    ];
  }

  if (moduleId) {
    const chapterTitle = config.resolveChapter(moduleId);
    if (!chapterTitle) {
      return [{ label: config.schoolLabel }];
    }
    return [
      { label: config.schoolLabel, onClick: goHub },
      { label: chapterTitle },
    ];
  }

  return [{ label: config.schoolLabel }];
}

export function LearnerLocationBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const learnNav = useParashariLearnNavOptional();
  const urlRevision = useParashariUrlRevision();
  const isParashariLearn = location.pathname.startsWith(ROUTES.PARASHARI_LEARN);
  /** Main school learn hub only - exclude bridge course routes under `/learn/courses/`. */
  const isJaiminiLearn = location.pathname === ROUTES.JAIMINI_LEARN;
  const isBnnLearn = location.pathname === ROUTES.BNN_LEARN;
  const isLalKitabLearn = location.pathname === ROUTES.LAL_KITAB_LEARN;
  const isKpLearn = location.pathname === ROUTES.KP_LEARN;

  const items = useMemo((): Crumb[] => {
    if (isParashariLearn) {
      const topicId = readJyotishaTopicFromLocation();
      const nakProfile = readNakProfileFromLocation();
      const currentPage = learnNav?.currentPage ?? 'jyotisha';

      const goHub = () => {
        clearJyotishaTopicFromUrl();
        learnNav?.setCurrentPage('jyotisha');
        window.dispatchEvent(new CustomEvent('jyotisha-reset-to-home'));
        if (!location.pathname.startsWith(ROUTES.PARASHARI_LEARN)) {
          navigate(ROUTES.PARASHARI_LEARN);
        }
      };

      const crumbs: Crumb[] = [{ label: 'Parashari', onClick: goHub }];

      if (currentPage === 'jyotish-marga' && !topicId) {
        crumbs.push({ label: 'Syllabus' });
        return crumbs;
      }

      if (!topicId) {
        crumbs.push({ label: 'Jyotisha' });
        return crumbs;
      }

      const chapter = getJyotishaTopicGroupLabelEn(topicId) || 'Chapter';
      const lesson = getJyotishaTopicLabel(topicId) || topicId;

      const groupId = groupIdForTopic(topicId);
      crumbs.push({
        label: chapter,
        onClick: groupId
          ? () => {
              replaceJyotishaTopicInUrl(null);
              learnNav?.setCurrentPage('jyotisha');
              window.setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent('jyotisha-open-milestone', { detail: { groupId } }),
                );
              }, 80);
            }
          : undefined,
      });
      crumbs.push({ label: lesson });

      if (nakProfile != null) {
        crumbs.push({ label: `Nakshatra ${nakProfile}` });
      }

      return crumbs;
    }

    if (isJaiminiLearn) {
      return buildSchoolLearnCrumbs(location.search, navigate, {
        learnPath: ROUTES.JAIMINI_LEARN,
        schoolLabel: 'Jaimini',
        resolveTopic: (topicId) => {
          const meta = getJaiminiTopic(topicId);
          if (!meta) return null;
          return {
            topicTitle: meta.topic.title.en,
            chapterId: meta.chapter.id,
            chapterTitle: meta.chapter.title.en,
          };
        },
        resolveChapter: (moduleId) => getJaiminiChapter(moduleId)?.title.en ?? null,
      });
    }

    if (isBnnLearn) {
      return buildSchoolLearnCrumbs(location.search, navigate, {
        learnPath: ROUTES.BNN_LEARN,
        schoolLabel: 'BNN',
        resolveTopic: (topicId) => {
          const meta = getBnnTopic(topicId);
          if (!meta) return null;
          return {
            topicTitle: meta.topic.title.en,
            chapterId: meta.chapter.id,
            chapterTitle: meta.chapter.title.en,
          };
        },
        resolveChapter: (moduleId) => getBnnChapter(moduleId)?.title.en ?? null,
      });
    }

    if (isLalKitabLearn) {
      return buildSchoolLearnCrumbs(location.search, navigate, {
        learnPath: ROUTES.LAL_KITAB_LEARN,
        schoolLabel: 'Lal Kitab',
        resolveTopic: (topicId) => {
          const meta = getLalKitabTopic(topicId);
          if (!meta) return null;
          return {
            topicTitle: meta.topic.title.en,
            chapterId: meta.chapter.id,
            chapterTitle: meta.chapter.title.en,
          };
        },
        resolveChapter: (moduleId) => getLalKitabChapter(moduleId)?.title.en ?? null,
      });
    }

    if (isKpLearn) {
      return buildSchoolLearnCrumbs(location.search, navigate, {
        learnPath: ROUTES.KP_LEARN,
        schoolLabel: 'KP',
        resolveTopic: (topicId) => {
          const meta = getKpTopic(topicId);
          if (!meta) return null;
          return {
            topicTitle: meta.topic.title.en,
            chapterId: meta.chapter.id,
            chapterTitle: meta.chapter.title.en,
          };
        },
        resolveChapter: (moduleId) => getKpChapter(moduleId)?.title.en ?? null,
      });
    }

    return [
      {
        label: learnerNav.sidebarTitle,
        onClick: () => navigate(ROUTES.DASHBOARD),
      },
      { label: resolveGenericPageLabel(location.pathname) },
    ];
    // urlRevision forces recompute after replaceState topic changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isParashariLearn,
    isJaiminiLearn,
    isBnnLearn,
    isLalKitabLearn,
    isKpLearn,
    learnNav,
    learnNav?.currentPage,
    location.pathname,
    location.search,
    navigate,
    urlRevision,
  ]);

  return (
    <div
      className={cn(
        'sticky top-16 z-[calc(var(--z-header)-1)] border-b border-border/80 bg-surface/95 backdrop-blur-md',
      )}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2 lg:px-8">
        <LocationTrail items={items} />
        {isParashariLearn ? (
          <Link
            to={ROUTES.ASSESSMENTS}
            className="ml-auto hidden shrink-0 text-xs font-medium text-accent-strong hover:underline sm:inline"
          >
            My Assessments
          </Link>
        ) : null}
      </div>
    </div>
  );
}
